import { ReactElement, useState } from "react";

import { FormCards } from "common/components/inputs/form/FormCards";
import { addFlowStepPrefix } from "common/utils";
import { CardsFlowData, CardsFlowStep } from "core/api";
import { FlowStepComponentProps } from "../models";
import { StyledStepContentWrapper } from "../steps.styles";

export type CardsProps = FlowStepComponentProps<CardsFlowStep, CardsFlowData>;

export type CardsSelectionType = "SINGLE" | "MULTI";
export type CardsSize = "SMALL" | "NORMAL";

/**
 * Cards step component
 *
 * Displays a list of card
 *
 */
export function Cards({ control, data, flowStep }: CardsProps): ReactElement {
  const { cardOptions, intSelectedIndices } = data;

  const [selectedIndices, setSelectedIndices] = useState<number[]>(
    intSelectedIndices ?? []
  );

  const selectionType = flowStep.config.selectionType;
  const cardSize = flowStep.config.cardSize;

  /**
   * Handles changes in the selected indices based on the provided update function.
   * It supports both single and multi-selection modes.
   *
   * @param updateFn A function or value to update the selected indices. If it's a function,
   *                 it will receive the previous indices and return the new ones. If it's a
   *                 direct value, it will replace the existing selected indices with the provided value.
   */
  const handleIndexChange = (updateFn: React.SetStateAction<number[]>) => {
    setSelectedIndices((prevIndices) => {
      const newIndices =
        typeof updateFn === "function" ? updateFn(prevIndices) : updateFn;

      // Handle the "SINGLE" selection type
      if (selectionType === "SINGLE") {
        return newIndices !== undefined ? newIndices : [];
      }

      // Handle the "MULTI" selection type
      return newIndices.filter((index) => index !== undefined);
    });
  };

  return (
    <StyledStepContentWrapper>
      <FormCards
        control={control}
        fieldName={addFlowStepPrefix(flowStep, "selectedCards")}
        options={cardOptions}
        defaultValue={selectedIndices}
        selectedIndices={selectedIndices}
        setSelectedIndices={handleIndexChange}
        selectionType={selectionType}
        cardSize={cardSize}
        required
        withSearch
      />
    </StyledStepContentWrapper>
  );
}
